import styled from 'styled-components';

export const StyledAssistantPage = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 60px);
  padding: 22px;
  background: ${({ theme }) => theme.colors.BG};
  box-sizing: border-box;
`;

export const StyledAssistantChat = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: ${({ theme }) => theme.colors.CARD};
  border: 1px solid ${({ theme }) => theme.colors.BORDER};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

export const StyledAssistantChatToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  gap: 8px;
  padding: 10px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.BORDER};
  background: ${({ theme }) => theme.colors.CARD};
`;

export const StyledAssistantClearAllButton = styled.button`
  border: none;
  background: transparent;
  padding: 4px 0;
  margin: 0;
  cursor: pointer;
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.LABEL};

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.TITLE};
  }

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }
`;

export const StyledAssistantClearAllIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.colors.BLUE.B400};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.BG1};
    color: ${({ theme }) => theme.colors.BLUE.B500};
  }

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

export const StyledThreadViewport = styled.div`
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const StyledHistoryLoader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 100%;
  min-height: 28px;
  padding: 4px 0;
`;

export const StyledThreadEmpty = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.LABEL};
  font-size: 14px;
  text-align: center;
  padding: 40px 20px;
`;

export const StyledTypingDots = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  height: 20px;
`;

export const StyledTypingDot = styled.span<{ $delay: number }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.BLUE.B400};
  opacity: 0.35;
  animation: assistantTypingBounce 1.2s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}s;

  @keyframes assistantTypingBounce {
    0%,
    60%,
    100% {
      transform: translateY(0);
      opacity: 0.35;
    }

    30% {
      transform: translateY(-5px);
      opacity: 1;
    }
  }
`;

export const StyledTypingLabel = styled.span`
  display: block;
  margin-top: 8px;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.LABEL};
`;

export const StyledMessageRow = styled.div<{ $isUser?: boolean }>`
  display: flex;
  justify-content: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

export const StyledAssistantTooltipWrapper = styled.div`
  position: relative;

  .tooltip {
    top: calc(100% + 4px);
    left: auto;
    right: 0;
    white-space: nowrap;
    z-index: 5;
  }

  &:hover .tooltip {
    visibility: visible;
    transition-delay: 0.15s;
  }
`;

export const StyledAssistantMessageDelete = styled.div`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  flex-shrink: 0;
  opacity: 0.55;

  &:hover {
    opacity: 1;
  }
`;

export const StyledMessageBubble = styled.div<{ $isUser?: boolean }>`
  width: fit-content;
  max-width: min(720px, 85%);
  padding: 12px 16px;
  border-radius: 8px;
  background: ${({ theme, $isUser }) =>
    $isUser ? theme.colors.BLUE.B400 : theme.colors.BG1};
  color: ${({ theme, $isUser }) =>
    $isUser ? theme.colors.NEUTRAL.N0 : theme.colors.TITLE};
  font-size: 14px;
  line-height: 20px;
  word-break: break-word;
  box-sizing: border-box;
  border: ${({ theme, $isUser }) =>
    $isUser ? 'none' : `1px solid ${theme.colors.BORDER}`};

  p {
    margin: 0 0 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul,
  ol {
    margin: 0 0 8px;
    padding-left: 20px;
  }
`;

export const StyledComposerRoot = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  padding: 16px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.BORDER};
  background: ${({ theme }) => theme.colors.CARD};
`;

export const StyledComposerInput = styled.textarea`
  flex: 1;
  min-height: 44px;
  max-height: 160px;
  resize: none;
  padding: 10px 14px;
  border: 1px solid ${({ theme }) => theme.colors.BORDER};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.BG};
  color: ${({ theme }) => theme.colors.TITLE};
  font-size: 14px;
  line-height: 20px;
  font-family: inherit;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: ${({ theme }) => theme.colors.BLUE.B400};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.LABEL};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const StyledComposerSend = styled.button<{ $isLoading?: boolean }>`
  min-width: 88px;
  height: 44px;
  padding: 0 16px;
  border: none;
  border-radius: 8px;
  background: ${({ theme, $isLoading }) =>
    $isLoading ? theme.colors.BLUE.B350 : theme.colors.BLUE.B400};
  color: ${({ theme }) => theme.colors.NEUTRAL.N0};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.BLUE.B500};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const StyledAssistantMessageStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  width: min(720px, 85%);
  min-width: 0;

  ${StyledMessageBubble} {
    max-width: 100%;
  }
`;

export const StyledCandidateResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: min(720px, 85%);

  ${StyledAssistantMessageStack} & {
    max-width: 100%;
  }
`;

export const StyledCandidateCard = styled.div<{ $clickable?: boolean }>`
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.colors.BORDER};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.BG};
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  box-sizing: border-box;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  ${({ $clickable, theme }) =>
    $clickable &&
    `
    &:hover {
      border-color: ${theme.colors.BLUE.B400};
    }
  `}
`;

export const StyledCandidateTitle = styled.div<{ $muted?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  color: ${({ theme, $muted }) =>
    $muted ? theme.colors.LABEL : theme.colors.TITLE};
  overflow: hidden;
`;

export const StyledCandidateSkills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const StyledCandidateSection = styled.div`
  font-size: 13px;
  line-height: 18px;
  color: ${({ theme }) => theme.colors.TITLE};
  min-width: 0;
`;

export const StyledCandidateSummary = styled(StyledCandidateSection)<{
  $clamp?: boolean;
}>`
  ${({ $clamp }) =>
    $clamp &&
    `
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    line-clamp: 3;
    overflow: hidden;
  `}
`;

export const StyledCandidateSectionTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.LABEL};
  margin-bottom: 4px;
`;

export const StyledCandidateAchievements = styled.ul`
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  line-height: 18px;
  color: ${({ theme }) => theme.colors.TITLE};
`;

export const StyledOpenCandidateLink = styled.a`
  align-self: flex-start;
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.BLUE.B400};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.BLUE.B500};
  }
`;

export const StyledToggleDetails = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.BLUE.B400};
  font-size: 12px;
  cursor: pointer;
  text-align: left;

  &:hover {
    color: ${({ theme }) => theme.colors.BLUE.B500};
  }
`;

export const StyledCandidateCardActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.BORDER};
`;

export const StyledCompactCandidateCard = styled.a`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.BORDER};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.CARD};
  text-align: left;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.BLUE.B400};
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  }
`;

export const StyledCompactCandidateInfo = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;

  ${StyledCandidateTitle} {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-clamp: 2;
    line-height: 18px;
    max-height: 36px;
    word-break: break-word;
  }
`;

export const StyledCompactMetaRole = styled.div`
  min-width: 0;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.LABEL};
`;

export const StyledCompactMetaSecondary = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
  width: 100%;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.LABEL};
`;

export const StyledCompactMetaFixed = styled.span`
  flex-shrink: 0;
  white-space: nowrap;
`;

export const StyledCompactMetaSep = styled.span`
  flex-shrink: 0;
`;

export const StyledCandidateCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-width: 0;
`;

export const StyledCandidateCardHeaderInfo = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const StyledMatchScoreBadge = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  padding-top: 1px;

  span {
    font-size: 10px;
    line-height: 12px;
    font-weight: 600;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.LABEL};
  }

  strong {
    font-size: 18px;
    line-height: 22px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.BLUE.B400};
  }
`;

export const StyledCompactMatchScore = styled.strong`
  flex-shrink: 0;
  font-size: 16px;
  line-height: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.BLUE.B400};
`;

export const StyledCompactCandidateHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  min-width: 0;
`;

export const StyledUserMessageRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  box-sizing: border-box;
  width: fit-content;
  max-width: min(720px, 85%);
  min-width: 0;
  padding-left: 38px;

  ${StyledMessageBubble} {
    max-width: 100%;
  }
`;

export const StyledOpenAssistantLink = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.BLUE.B400};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.BLUE.B500};
  }
`;
